import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod/v4";
import { billOfMaterialsIdFormat, productIdFormat } from "../ids";
import { MoneySchema, WeightSchema, WeightUnit } from "../outbound-labels/util";
import { RedoShopifyCountryCode } from "./fulfillment-country-code";
import { FulfillmentGroupData } from "./fulfillment-group";

const FulfillmentOrderLineItemSchemaBase = z.object({
  externalId: z.string(), // SHOULD BE GLOBALLY UNIQUE
  imageUrl: z.string().nullish(),
  customAttributes: z.array(z.object({ key: z.string(), value: z.string() })),
  // Redo business logic should only use quantity
  // unfulfilledQuantity and totalQuantity are provided by the marketplace and are used
  // in upsert logic to calculate the "quantity" of items when we split fulfillment orders across
  // multiple fulfillment groups (ex. partial fulfillments)
  totalQuantity: z.number(),
  unfulfilledQuantity: z.number(),
  quantity: z.number(),
  weight: z.object({
    unit: createEnum(WeightUnit, { enumName: "WeightUnit" }),
    value: z.number(),
  }),
  // These three IDs are nullish because if the product is deleted they all become null
  productId: z.string().nullish(),
  variantId: z.string().nullish(),
  inventoryItemId: z.string().nullish(),
  redoProductId: productIdFormat.schema.nullish(),
  inventoryItemWeight: WeightSchema.nullish(),
  lineItemId: z.string(),
  title: z.string(),
  productTitle: z.string(),
  variantTitle: z.string(),
  variantInventoryQuantity: z.number().nullish(),
  selectedOptions: z
    .array(z.object({ name: z.string(), value: z.string() }))
    .nullish(),
  requiresShipping: z.boolean().nullish(),
  sku: z.string().nullish(),
  unitPrice: MoneySchema,
  unitDiscountedPrice: MoneySchema,
  totalPrice: MoneySchema,
  totalDiscountedPrice: MoneySchema,
  totalTax: MoneySchema,
  hsCode: z.string().nullable(),
  countryCodeOfOrigin: createEnum(RedoShopifyCountryCode, {
    enumName: "RedoShopifyCountryCode",
  }).nullish(),
  bin: z.string().nullish(),
  barcode: z.string().nullish(),
  productTags: z.array(z.string()).nullish(),
  vendor: z.string().nullish(),
  description: z.string().nullish(),
  notReceived: z.boolean().nullish(),
  productCustomDate1: z.string().nullish(),
  productCustomDate2: z.string().nullish(),
  productCustomDate3: z.string().nullish(),
  productCustomString1: z.string().nullish(),
  productCustomString2: z.string().nullish(),
  productCustomString3: z.string().nullish(),
  productCustomNumber1: z.string().nullish(),
  productCustomNumber2: z.string().nullish(),
  productCustomNumber3: z.string().nullish(),
  variantCustomDate1: z.string().nullish(),
  variantCustomDate2: z.string().nullish(),
  variantCustomDate3: z.string().nullish(),
  variantCustomString1: z.string().nullish(),
  variantCustomString2: z.string().nullish(),
  variantCustomString3: z.string().nullish(),
  variantCustomNumber1: z.string().nullish(),
  variantCustomNumber2: z.string().nullish(),
  variantCustomNumber3: z.string().nullish(),
  orderId: z.string().nullish(),
  lineItemNumber: z.string().nullish(),
  preSplitUnfulfilledQuantity: z.number().nullish(), // Used to ensure that we dont duplicate line items for split orders
  dimValue: z.number().nullish(),
  productType: z.string().nullish(),
  imsInventoryItemId: z.string().nullish(),
  shouldBeInOwnPackage: z.boolean().nullish(),
});

export interface BillOfMaterialsInfo {
  bomId: z.infer<typeof billOfMaterialsIdFormat.schema>;
  expandedQuantity: number;
  expansionItems: FulfillmentOrderLineItem[];
  calculatedAt?: Date;
  /** When true, a work order flow handles this BOM's inputs — do not expand for picking */
  hasWorkOrderFlow?: boolean;
  leftovers?: {
    redoProductId: z.infer<typeof productIdFormat.schema>;
    quantity: number;
  }[];
}

/**
 * WARNING: Line items may contain sub items from Bill of Materials (BOM) expansion via `billOfMaterialsInfos`.
 * If the use case is related to picking items (packing slips, pick lists, scanning, pick sessions, etc) - you probably want to use `expandLineItemsToPickableItems()` to get only the leaf node items.
 * In most cases (splitting, table filtering, etc) - you probably want to use the line items as-is - the actual line items that were ordered.
 */
export type FulfillmentOrderLineItem = z.infer<
  typeof FulfillmentOrderLineItemSchemaBase
> & { billOfMaterialsInfos?: BillOfMaterialsInfo[] };

// Extend base schema with billOfMaterialsInfos (must use z.custom to handle recursive reference)
export const FulfillmentOrderLineItemSchema_ =
  FulfillmentOrderLineItemSchemaBase.extend({
    // Use z.custom with JSON annotation to prevent recursive schema extraction in DDL generator
    // while maintaining correct TypeScript types. The recursive structure will be stored as JSONB.
    billOfMaterialsInfos: z
      .custom<BillOfMaterialsInfo[]>()
      .meta({ annotation: "JSON" })
      .optional(),
  });

// Export with type annotation for type safety
export const FulfillmentOrderLineItemSchema: z.ZodType<FulfillmentOrderLineItem> =
  FulfillmentOrderLineItemSchema_;

export interface IFulfillmentOrderLineItem
  extends z.ZodType<FulfillmentOrderLineItem> {}

export type FulfillmentOrderLineItemWithOrderIds = FulfillmentOrderLineItem & {
  orderNames: string[];
};

interface LineItemGroupingConfig {
  usePrice?: boolean;
  degroupByCustomAttributes?: boolean;
  fallbackToTitle?: boolean;
}

export function getLineItemGroupingKey(
  item: FulfillmentOrderLineItem,
  config?: LineItemGroupingConfig,
): string {
  if (!item.variantId || !item.unitDiscountedPrice.amount) {
    if (config?.fallbackToTitle) {
      return item.title;
    }
    return item.externalId;
  }
  const customAttributesKey = [...item.customAttributes]
    .sort((a, b) => a.key.localeCompare(b.key))
    .map((attr) => `${attr.key}:${attr.value}`)
    .join("|");

  let key = `${item.variantId}`;
  if (config?.usePrice) {
    key += `-${item.unitDiscountedPrice.amount}`;
  }
  if (config?.degroupByCustomAttributes) {
    key += `-${customAttributesKey}`;
  }
  return key;
}

// We could have the same item purchased on multiple orders that get merged together - this should reduce those line items to a single line item
export function groupFulfillmentOrderLineItems(
  items: readonly FulfillmentOrderLineItem[],
  config?: LineItemGroupingConfig,
): FulfillmentOrderLineItem[] {
  const reducedLineItems: FulfillmentOrderLineItem[] = [];
  const reducedLineItemsRec: Record<string, FulfillmentOrderLineItem> = {};

  for (const item of items) {
    const key = getLineItemGroupingKey(item, config);

    if (!reducedLineItemsRec[key]) {
      reducedLineItemsRec[key] = { ...item };
    } else {
      reducedLineItemsRec[key].totalQuantity += item.totalQuantity;
      reducedLineItemsRec[key].unfulfilledQuantity += item.unfulfilledQuantity;
      reducedLineItemsRec[key].quantity += item.quantity;
    }
  }

  return [...reducedLineItems, ...Object.values(reducedLineItemsRec)];
}

// Given a list of line items grouped together but that may not be grouped by fulfillment order, return a list of line items ungrouped. The list will only have one entry for each fulfillment order line item id
export function ungroupFulfillmentOrderLineItems(
  originalItems: FulfillmentOrderLineItem[],
  groupedItems: FulfillmentOrderLineItem[],
): FulfillmentOrderLineItem[] {
  const ungrouped: FulfillmentOrderLineItem[] = [];

  for (const originalItem of originalItems) {
    const matchingGroupedItem = groupedItems.find(
      (i) =>
        !!i.variantId &&
        i.variantId === originalItem.variantId &&
        i.unitDiscountedPrice.amount ===
          originalItem.unitDiscountedPrice.amount,
    );
    if (matchingGroupedItem && matchingGroupedItem.quantity > 0) {
      const quantityToTake = Math.min(
        originalItem.quantity,
        matchingGroupedItem.quantity,
      );

      // Remove the quantity from the grouped item and add that to the ungrouped item
      matchingGroupedItem.quantity -= quantityToTake;
      ungrouped.push({ ...originalItem, quantity: quantityToTake });
    }
  }

  for (const groupedItem of groupedItems) {
    if (groupedItem.quantity > 0) {
      ungrouped.push(groupedItem);
    }
  }

  return ungrouped;
}

export function getLineItemKey(item: FulfillmentOrderLineItem): string {
  return `${item.productId}${item.variantId ? `-${item.variantId}` : ""}`;
}

export function calculatePickedQuantitiesByGroupingKey(
  items: readonly FulfillmentOrderLineItem[],
  pickedQuantityByExternalId: Map<string, number>,
  config?: { usePrice?: boolean; degroupByCustomAttributes?: boolean },
): Map<string, number> {
  const pickedByGroupingKey = new Map<string, number>();

  for (const item of items) {
    const groupingKey = getLineItemGroupingKey(item, config);
    const pickedQty = pickedQuantityByExternalId.get(item.externalId) || 0;
    pickedByGroupingKey.set(
      groupingKey,
      (pickedByGroupingKey.get(groupingKey) || 0) + pickedQty,
    );
  }

  return pickedByGroupingKey;
}

export function getPickedMapByExternalId(
  fulfillmentGroup: FulfillmentGroupData,
): Map<string, number> {
  const pickSessionItemStatuses =
    fulfillmentGroup.pickSessionItemStatuses || [];
  const pickedMap = new Map<string, number>();

  for (const status of pickSessionItemStatuses) {
    pickedMap.set(
      status.fulfillmentOrderLineItemExternalId,
      status.quantityPicked,
    );
  }

  return pickedMap;
}

export function getMissingPickedItems(
  fulfillmentGroup: FulfillmentGroupData,
  options: { hideInputItems: boolean },
): FulfillmentOrderLineItem[] {
  const pickedByExternalId = getPickedMapByExternalId(fulfillmentGroup);

  const pickableItems = expandLineItemsToPickableItems(
    fulfillmentGroup.items,
    options,
  );
  const missingItems: FulfillmentOrderLineItem[] = [];
  for (const item of pickableItems) {
    // If item is not in pickSessionItemStatuses, treat as unpicked (quantity 0)
    const pickedQty = pickedByExternalId.get(item.externalId) || 0;

    if (pickedQty < item.quantity) {
      missingItems.push({ ...item, quantity: item.quantity - pickedQty });
    }
  }

  // Expand for consistency with item name column so that we show the total quantity of items in the column
  // We cannot use getExpandedItems function because it has a cache for the fulfillment group items and we need to display a subset of the items
  const expanded = missingItems.flatMap((item) =>
    Array.from({ length: item.quantity }, () => ({
      ...item,
      quantity: 1,
      totalQuantity: 1,
    })),
  );

  return expanded;
}

/**
 * Recursively collects pickable items from a tree structure.
 * For partially expanded BOMs (expandedQuantity < quantity), the parent item
 * is also returned as a pickable item with the non-expanded quantity.
 *
 * @param item - The item to extract pickable items from
 * @param options.hideInputItems - When true, treat every BOM expansion the same
 *   as a work-order-handled BOM: keep the parent output as the pickable item
 *   and skip descending into the expansion inputs.
 * @returns Array of pickable items
 */
function collectBomLeafNodes(
  item: FulfillmentOrderLineItem,
  options: { hideInputItems: boolean },
): FulfillmentOrderLineItem[] {
  const bomInfos = item.billOfMaterialsInfos;
  const hasChildren = bomInfos?.some((info) => info.expansionItems.length > 0);

  if (!hasChildren) {
    return [item];
  }

  const results: FulfillmentOrderLineItem[] = [];

  // Count only non-work-order expansions toward the expanded quantity.
  // Work-order BOMs keep the output product as the pickable item, so their
  // expandedQuantity should remain as parent (output) quantity, not be subtracted.
  // When hideInputItems is on, treat every BOM the same way.
  const expandedQtyWithoutWorkOrders = bomInfos!.reduce(
    (sum, info) =>
      sum +
      (info.hasWorkOrderFlow || options.hideInputItems
        ? 0
        : info.expandedQuantity),
    0,
  );
  const nonExpandedQty = item.quantity - expandedQtyWithoutWorkOrders;
  if (nonExpandedQty > 0) {
    results.push({
      ...item,
      quantity: nonExpandedQty,
      totalQuantity: nonExpandedQty,
      unfulfilledQuantity: nonExpandedQty,
      billOfMaterialsInfos: undefined,
    });
  }

  // Collect leaf nodes from non-work-order BOM expansions' children
  for (const bomInfo of bomInfos!) {
    // Skip expansions handled by work orders — the output product is included
    // above as part of the non-expanded parent quantity. Same when the
    // hideInputItems setting is on.
    if (bomInfo.hasWorkOrderFlow || options.hideInputItems) {
      continue;
    }
    results.push(
      ...bomInfo.expansionItems.flatMap((child) =>
        collectBomLeafNodes(child, options),
      ),
    );
  }

  return results;
}

/**
 * Extracts only the pickable items from BOM expanded line items.
 * When items have billOfMaterialsInfos (bundles/kits), returns the actual components to pick.
 * For partially expanded items (expandedQuantity < quantity), the parent item
 * is also returned as a pickable item with the non-expanded quantity.
 *
 * Example (full expansion):
 * - Item A (qty=2, expandedQuantity=2) -> [B, C]
 * Result: [B, C]
 *
 * Example (partial expansion):
 * - Item A (qty=3, expandedQuantity=1) -> [B, C]
 * Result: [A (qty=2), B, C]
 *
 * @param items - Array of line items that may have BOM expansion
 * @param options.hideInputItems - When true, do not expand BOMs into their
 *   input items (keep the parent output item as pickable). Required so callers
 *   must explicitly opt in or out — display paths typically read this from the
 *   team's `outboundLabels.billOfMaterials.alwaysHideInputItems` setting,
 *   while inventory/correctness paths must always pass `false`.
 * @returns Array of only the leaf node items (actual components to pick)
 */
export function expandLineItemsToPickableItems(
  items: readonly FulfillmentOrderLineItem[],
  options: { hideInputItems: boolean },
): FulfillmentOrderLineItem[] {
  return items.flatMap((item) => collectBomLeafNodes(item, options));
}

export type BomLeftover = NonNullable<BillOfMaterialsInfo["leftovers"]>[number];

/**
 * Recursively collects leftovers from a single line item's BOM tree.
 */
function collectBomLeftoversFromItem(
  item: FulfillmentOrderLineItem,
): BomLeftover[] {
  const bomInfos = item.billOfMaterialsInfos;
  if (!bomInfos) {
    return [];
  }

  const results: BomLeftover[] = [];

  for (const bomInfo of bomInfos) {
    if (bomInfo.leftovers) {
      results.push(...bomInfo.leftovers);
    }
    for (const child of bomInfo.expansionItems) {
      results.push(...collectBomLeftoversFromItem(child));
    }
  }

  return results;
}

/**
 * Recursively collects all BOM leftovers from a list of line items.
 * Traverses the full BOM tree to find leftovers at every nesting level.
 *
 * @param items - Array of line items that may have BOM expansion with leftovers
 * @returns Array of all leftovers found across all BOM levels
 */
export function collectAllBomLeftovers(
  items: readonly FulfillmentOrderLineItem[],
): BomLeftover[] {
  return items.flatMap((item) => collectBomLeftoversFromItem(item));
}
